<script setup>
// SiteUp — Personalizar Modo Foco: modo de configuração da tela (todas as
// áreas visíveis), painel com abas, holofote e apply imediato. Ao sair,
// devolve o layout exatamente como estava.
import { ref, computed, nextTick, onBeforeUnmount, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import FocusSpotlight from './FocusSpotlight.vue';
import { useFocusSpotlight } from 'dashboard/composables/useFocusSpotlight';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import { useWorkbenchLayout } from 'dashboard/composables/useWorkbenchLayout';
import BrasaMascot from 'dashboard/components-next/onboarding/BrasaMascot.vue';
import {
  FOCUS_MENU_SIDES,
  useFocusMode,
} from 'dashboard/composables/useFocusMode';
import {
  CONV_LIST_FONT_SIZES,
  CONV_LIST_FONT_SIZE_DEFAULT,
  useConversationListFontSize,
} from 'dashboard/composables/useConversationListFontSize';
import {
  useUISettings,
  DEFAULT_CONVERSATION_SIDEBAR_HIDDEN_ITEMS,
} from 'dashboard/composables/useUISettings';
import { CONVERSATION_SIDEBAR_SECTIONS } from 'dashboard/routes/dashboard/conversation/conversationSidebarSections';
import {
  COLOR_SCHEMES,
  applyColorScheme,
  getColorScheme,
} from 'dashboard/helper/themeHelper';

const { isWorkspaceV2 } = useWorkspaceV2();
const TAB_IDS = [
  'theme',
  'list',
  'header',
  'reply',
  'contact',
  'panel',
  'cluster',
];
const tabIds = computed(() =>
  isWorkspaceV2.value ? TAB_IDS.filter(id => id !== 'cluster') : TAB_IDS
);
const COMPOSER_SELECTOR = '[data-conversation-composer], .reply-box';

const TARGET_SELECTOR = {
  theme: '',
  header: '[data-testid="focus-spot-header"]',
  reply: COMPOSER_SELECTOR,
  contact: '[data-testid="focus-spot-contact-card"]',
  panel: '[data-testid="focus-spot-sidebar"]',
  cluster: '[data-testid="focus-mode-cluster"]',
};

const GROUPS = {
  header: {
    key: 'conversation_header_hidden_items',
    items: [
      { name: 'tasks', labelKey: 'SIDEBAR.FOCUS_MODE.ITEMS.TASKS' },
      { name: 'agenda', labelKey: 'SIDEBAR.FOCUS_MODE.ITEMS.AGENDA' },
      { name: 'sla', labelKey: 'SIDEBAR.FOCUS_MODE.ITEMS.SLA' },
      { name: 'call_button', labelKey: 'SIDEBAR.FOCUS_MODE.ITEMS.CALL' },
    ],
  },
  reply: {
    key: 'conversation_reply_hidden_items',
    items: [
      { name: 'emoji', labelKey: 'SIDEBAR.FOCUS_MODE.ITEMS.EMOJI' },
      { name: 'attach', labelKey: 'SIDEBAR.FOCUS_MODE.ITEMS.ATTACH' },
      { name: 'audio_recorder', labelKey: 'SIDEBAR.FOCUS_MODE.ITEMS.AUDIO' },
      { name: 'signature', labelKey: 'SIDEBAR.FOCUS_MODE.ITEMS.SIGNATURE' },
      { name: 'video_call', labelKey: 'SIDEBAR.FOCUS_MODE.ITEMS.VIDEO' },
      { name: 'insert_article', labelKey: 'SIDEBAR.FOCUS_MODE.ITEMS.ARTICLE' },
    ],
  },
  contact: {
    key: 'conversation_pinned_hidden_items',
    items: [
      {
        name: 'participants',
        labelKey: 'SIDEBAR.FOCUS_MODE.ITEMS.PARTICIPANTS',
      },
      { name: 'team', labelKey: 'SIDEBAR.FOCUS_MODE.ITEMS.TEAM' },
      { name: 'labels', labelKey: 'SIDEBAR.FOCUS_MODE.ITEMS.LABELS' },
    ],
  },
  panel: {
    key: 'conversation_sidebar_hidden_items',
    items: CONVERSATION_SIDEBAR_SECTIONS,
  },
};

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();
const { menuSide, setMenuSide } = useFocusMode();
const { currentSize: listFontSize, setFontSize: setListFontSize } =
  useConversationListFontSize();
const startFocusModeTour = inject('startFocusModeTour', () => {});
const {
  spotlightStyle,
  setSelector,
  measure,
  attach: attachSpotlight,
  detach: detachSpotlight,
  scheduleMeasure,
} = useFocusSpotlight();

const isOpen = ref(false);
const confirmingRestore = ref(false);
const activeTab = ref('theme');
const panelRef = ref(null);
const colorScheme = ref(getColorScheme());

let layoutSnapshot = null;
let ignoreOutsideClick = false;

const tabLabel = id => {
  if (id === 'theme') return t('SIDEBAR.FOCUS_MODE.TAB_THEME');
  if (id === 'list') return t('SIDEBAR.FOCUS_MODE.TAB_LIST');
  if (id === 'header') return t('SIDEBAR.FOCUS_MODE.TAB_HEADER');
  if (id === 'reply') return t('SIDEBAR.FOCUS_MODE.TAB_REPLY');
  if (id === 'contact') return t('SIDEBAR.FOCUS_MODE.TAB_CONTACT');
  if (id === 'panel') return t('SIDEBAR.FOCUS_MODE.TAB_PANEL');
  return t('SIDEBAR.FOCUS_MODE.TAB_CLUSTER');
};

const paneTitle = id => {
  if (id === 'theme') return t('SIDEBAR.FOCUS_MODE.GROUP_THEME');
  if (id === 'list') return t('SIDEBAR.FOCUS_MODE.GROUP_LIST');
  if (id === 'header') return t('SIDEBAR.FOCUS_MODE.GROUP_HEADER');
  if (id === 'reply') return t('SIDEBAR.FOCUS_MODE.GROUP_REPLY');
  if (id === 'contact') return t('SIDEBAR.FOCUS_MODE.GROUP_CONTACT_CARD');
  if (id === 'panel') return t('SIDEBAR.FOCUS_MODE.GROUP_CONVERSATION_PANEL');
  return t('SIDEBAR.FOCUS_MODE.MENU_SIDE');
};

const paneHint = id => {
  if (id === 'theme') return t('SIDEBAR.FOCUS_MODE.HINT_THEME');
  if (id === 'list') return t('SIDEBAR.FOCUS_MODE.HINT_LIST');
  if (id === 'header') return t('SIDEBAR.FOCUS_MODE.HINT_HEADER');
  if (id === 'reply') return t('SIDEBAR.FOCUS_MODE.HINT_REPLY');
  if (id === 'contact') return t('SIDEBAR.FOCUS_MODE.HINT_CONTACT');
  if (id === 'panel') return t('SIDEBAR.FOCUS_MODE.HINT_PANEL');
  return t('SIDEBAR.FOCUS_MODE.HINT_CLUSTER');
};

const sideLabel = side => {
  if (side === 'auto') return t('SIDEBAR.FOCUS_MODE.MENU_SIDE_AUTO');
  if (side === 'up') return t('SIDEBAR.FOCUS_MODE.MENU_SIDE_UP');
  if (side === 'down') return t('SIDEBAR.FOCUS_MODE.MENU_SIDE_DOWN');
  if (side === 'left') return t('SIDEBAR.FOCUS_MODE.MENU_SIDE_LEFT');
  return t('SIDEBAR.FOCUS_MODE.MENU_SIDE_RIGHT');
};

const themeLabel = scheme => {
  if (scheme === 'light') return t('SIDEBAR.FOCUS_MODE.THEME_LIGHT');
  if (scheme === 'dark') return t('SIDEBAR.FOCUS_MODE.THEME_DARK');
  return t('SIDEBAR.FOCUS_MODE.THEME_AUTO');
};

const listFontLabel = size => {
  if (size === 'sm') return t('SIDEBAR.FOCUS_MODE.LIST_FONT_SM');
  if (size === 'lg') return t('SIDEBAR.FOCUS_MODE.LIST_FONT_LG');
  return t('SIDEBAR.FOCUS_MODE.LIST_FONT_MD');
};

const setTheme = scheme => {
  colorScheme.value = applyColorScheme(scheme);
};

// Redesign 23/08/2026 (pedido do Lucas: "ta muito simplizinho"): rail com
// icone por area, tema com mini previa, modo da lista aqui (Lista e So fotos
// sairam do menu do Brasa), tamanho do texto e lado do menu como controles
// visuais, toggles com icone por item. A logica de persistencia nao mudou.
const TAB_ICONS = {
  theme: 'i-ri-contrast-2-line',
  list: 'i-ri-list-check-2',
  header: 'i-ri-layout-top-line',
  reply: 'i-ri-chat-new-line',
  contact: 'i-ri-contacts-line',
  panel: 'i-ri-layout-right-line',
  cluster: 'i-ri-focus-3-line',
};
const ITEM_ICONS = {
  tasks: 'i-ri-checkbox-line',
  agenda: 'i-ri-calendar-2-line',
  sla: 'i-ri-timer-line',
  call_button: 'i-ri-phone-line',
  emoji: 'i-ri-emotion-line',
  attach: 'i-ri-attachment-2',
  audio_recorder: 'i-ri-mic-line',
  signature: 'i-ri-quill-pen-line',
  video_call: 'i-ri-video-chat-line',
  insert_article: 'i-ri-article-line',
  participants: 'i-ri-group-line',
  team: 'i-ri-team-line',
  labels: 'i-ri-price-tag-3-line',
  kanban_panel: 'i-ri-kanban-view',
  whatsapp: 'i-ri-whatsapp-line',
  lead: 'i-ri-user-star-line',
  lead_score: 'i-ri-bar-chart-2-line',
  shared_files: 'i-ri-folder-line',
  macros: 'i-ri-flashlight-line',
  conversation_info: 'i-ri-information-line',
  contact_attributes: 'i-ri-list-settings-line',
  contact_notes: 'i-ri-sticky-note-line',
  previous_conversation: 'i-ri-history-line',
  linear_issues: 'i-ri-git-branch-line',
  shopify_orders: 'i-ri-shopping-bag-line',
  jornada_360: 'i-ri-route-line',
};
const itemIcon = item =>
  ITEM_ICONS[item.name] || 'i-ri-checkbox-blank-circle-line';
const LIST_MODES = [
  { id: 'list', icon: 'i-ri-list-check' },
  { id: 'avatars', icon: 'i-ri-user-line' },
  { id: 'hidden', icon: 'i-ri-aspect-ratio-line' },
];
const { listMode, setListMode } = useWorkbenchLayout();
const listModeLabel = id => {
  if (id === 'list') return t('CHAT_LIST.WORKBENCH.MODE_LIST');
  if (id === 'avatars') return t('CHAT_LIST.WORKBENCH.MODE_AVATARS');
  return t('CHAT_LIST.WORKBENCH.MODE_HIDDEN');
};
// Lado do menu como bussola: cima, esquerda, auto (centro), direita, baixo.
const SIDE_GRID = [
  null,
  'up',
  null,
  'left',
  'auto',
  'right',
  null,
  'down',
  null,
].map(side => (side && FOCUS_MENU_SIDES.includes(side) ? side : null));
const sideIcon = side => {
  if (side === 'up') return 'i-ri-arrow-up-line';
  if (side === 'down') return 'i-ri-arrow-down-line';
  if (side === 'left') return 'i-ri-arrow-left-line';
  if (side === 'right') return 'i-ri-arrow-right-line';
  return 'i-ri-focus-3-line';
};

const activeGroup = computed(() => GROUPS[activeTab.value] || null);
const activeItems = computed(() => activeGroup.value?.items || []);

const hiddenFor = key => {
  const hidden = uiSettings.value?.[key];
  return Array.isArray(hidden) ? hidden : [];
};

const isVisible = (key, name) => !hiddenFor(key).includes(name);

const visibleCount = id => {
  const group = GROUPS[id];
  if (!group) return null;
  const total = group.items.length;
  const shown = group.items.filter(item =>
    isVisible(group.key, item.name)
  ).length;
  return { shown, total };
};

const labelFor = item => {
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.SLA') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.SLA');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.CALL') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.CALL');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.EMOJI') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.EMOJI');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.ATTACH') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.ATTACH');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.AUDIO') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.AUDIO');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.SIGNATURE') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.SIGNATURE');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.VIDEO') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.VIDEO');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.ARTICLE') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.ARTICLE');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.PARTICIPANTS') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.PARTICIPANTS');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.TEAM') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.TEAM');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.LABELS') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.LABELS');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.KANBAN') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.KANBAN');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.WHATSAPP') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.WHATSAPP');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.TASKS') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.TASKS');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.AGENDA') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.AGENDA');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.LEAD') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.LEAD');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.LEAD_SCORE') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.LEAD_SCORE');
  }
  if (item.labelKey === 'SIDEBAR.FOCUS_MODE.ITEMS.SHARED_FILES') {
    return t('SIDEBAR.FOCUS_MODE.ITEMS.SHARED_FILES');
  }
  if (item.labelKey === 'JORNADA_360.TITLE') return t('JORNADA_360.TITLE');
  if (item.labelKey === 'CONVERSATION_SIDEBAR.ACCORDION.MACROS') {
    return t('CONVERSATION_SIDEBAR.ACCORDION.MACROS');
  }
  if (item.labelKey === 'CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO') {
    return t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO');
  }
  if (item.labelKey === 'CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES') {
    return t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES');
  }
  if (item.labelKey === 'CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES') {
    return t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_NOTES');
  }
  if (
    item.labelKey === 'CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION'
  ) {
    return t('CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION');
  }
  if (item.labelKey === 'CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE') {
    return t('CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE');
  }
  if (item.labelKey === 'CONVERSATION_SIDEBAR.ACCORDION.LINEAR_ISSUES') {
    return t('CONVERSATION_SIDEBAR.ACCORDION.LINEAR_ISSUES');
  }
  if (item.labelKey === 'CONVERSATION_SIDEBAR.ACCORDION.SHOPIFY_ORDERS') {
    return t('CONVERSATION_SIDEBAR.ACCORDION.SHOPIFY_ORDERS');
  }
  return item.name;
};

const snapshotLayout = () => {
  const settings = uiSettings.value || {};
  layoutSnapshot = {
    is_contact_sidebar_open: settings.is_contact_sidebar_open === true,
    is_copilot_panel_open: settings.is_copilot_panel_open === true,
    workbench_list_mode: settings.workbench_list_mode,
  };
};

const applyConfigLayout = () => {
  const settings = uiSettings.value || {};
  const patch = {};
  if (settings.is_contact_sidebar_open !== true) {
    patch.is_contact_sidebar_open = true;
  }
  if (settings.is_copilot_panel_open === true) {
    patch.is_copilot_panel_open = false;
  }
  if (Object.keys(patch).length) updateUISettings(patch);
};

const restoreLayout = () => {
  if (!layoutSnapshot) return;
  const patch = {
    is_contact_sidebar_open: layoutSnapshot.is_contact_sidebar_open,
    is_copilot_panel_open: layoutSnapshot.is_copilot_panel_open,
  };
  if (layoutSnapshot.workbench_list_mode !== undefined) {
    patch.workbench_list_mode = layoutSnapshot.workbench_list_mode;
  }
  updateUISettings(patch);
  layoutSnapshot = null;
};

const measureSpotlight = () => {
  setSelector(TARGET_SELECTOR[activeTab.value]);
  measure();
};

const setVisibility = (key, name, visible) => {
  const next = new Set(hiddenFor(key));
  if (visible) next.delete(name);
  else next.add(name);
  updateUISettings({ [key]: Array.from(next) });
  nextTick(measureSpotlight);
};

let onDocKeydown;
let onDocClick;

function detachObservers() {
  detachSpotlight();
  if (typeof document !== 'undefined') {
    document.removeEventListener('keydown', onDocKeydown);
    document.removeEventListener('click', onDocClick);
  }
}

function attachObservers() {
  detachObservers();
  if (typeof document === 'undefined') return;
  attachSpotlight([
    TARGET_SELECTOR[activeTab.value],
    TARGET_SELECTOR.header,
    COMPOSER_SELECTOR,
    TARGET_SELECTOR.panel,
  ]);
  document.addEventListener('keydown', onDocKeydown);
  document.addEventListener('click', onDocClick);
}

function close() {
  if (!isOpen.value) return;
  isOpen.value = false;
  confirmingRestore.value = false;
  ignoreOutsideClick = false;
  restoreLayout();
  detachObservers();
}

onDocKeydown = event => {
  if (!isOpen.value || event.key !== 'Escape') return;
  event.preventDefault();
  if (confirmingRestore.value) {
    confirmingRestore.value = false;
    return;
  }
  close();
};

onDocClick = event => {
  if (!isOpen.value || ignoreOutsideClick) return;
  if (panelRef.value?.contains(event.target)) return;
  close();
};

const focusActiveTab = () => {
  panelRef.value?.querySelector('[role="tab"][aria-selected="true"]')?.focus();
};

const selectTab = id => {
  if (!tabIds.value.includes(id)) return;
  activeTab.value = id;
  confirmingRestore.value = false;
  nextTick(() => {
    measureSpotlight();
    attachObservers();
    focusActiveTab();
  });
};

const onTablistKeydown = event => {
  const current = tabIds.value.indexOf(activeTab.value);
  let next = current;
  if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
    next = (current + 1) % tabIds.value.length;
  } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
    next = (current - 1 + tabIds.value.length) % tabIds.value.length;
  } else if (event.key === 'Home') {
    next = 0;
  } else if (event.key === 'End') {
    next = tabIds.value.length - 1;
  } else {
    return;
  }
  event.preventDefault();
  selectTab(tabIds.value[next]);
};

const open = () => {
  snapshotLayout();
  applyConfigLayout();
  isOpen.value = true;
  confirmingRestore.value = false;
  ignoreOutsideClick = true;
  activeTab.value = 'theme';
  colorScheme.value = getColorScheme();
  nextTick(() => {
    measureSpotlight();
    attachObservers();
    focusActiveTab();
    if (typeof window === 'undefined') return;
    window.setTimeout(() => {
      ignoreOutsideClick = false;
      scheduleMeasure();
    }, 80);
  });
};

const replayGuide = () => {
  close();
  nextTick(() => startFocusModeTour());
};

const askRestore = () => {
  confirmingRestore.value = true;
};

const cancelRestore = () => {
  confirmingRestore.value = false;
};

const restoreDefaults = () => {
  updateUISettings({
    conversation_header_hidden_items: [],
    conversation_reply_hidden_items: [],
    conversation_pinned_hidden_items: [],
    conversation_sidebar_hidden_items: [
      ...DEFAULT_CONVERSATION_SIDEBAR_HIDDEN_ITEMS,
    ],
    focus_mode_menu_side: 'auto',
    focus_mode_dock_position: null,
    conversation_list_font_size: isWorkspaceV2.value
      ? 'md'
      : CONV_LIST_FONT_SIZE_DEFAULT,
  });
  confirmingRestore.value = false;
  nextTick(measureSpotlight);
};

onBeforeUnmount(() => {
  if (isOpen.value) restoreLayout();
  detachObservers();
});

defineExpose({ open, close });
</script>

<template>
  <TeleportWithDirection to="body">
    <template v-if="isOpen">
      <FocusSpotlight :style-value="spotlightStyle" />

      <div
        ref="panelRef"
        role="dialog"
        aria-modal="true"
        aria-labelledby="focus-mode-settings-title"
        class="fixed left-1/2 top-1/2 z-[80] flex max-h-[min(36rem,calc(100vh-2rem))] w-[min(42rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-3xl border border-n-weak bg-n-solid-1 shadow-2xl"
        data-testid="focus-mode-settings"
        :class="{
          'font-chirp [&_.font-bold]:!font-chirp [&_.font-medium]:!font-chirp max-sm:flex-col [&_button]:min-h-11':
            isWorkspaceV2,
        }"
      >
        <div
          role="tablist"
          :class="{
            'max-sm:w-full max-sm:flex-row max-sm:overflow-x-auto max-sm:border-b max-sm:border-r-0 [&>button]:max-sm:w-auto [&>button]:max-sm:shrink-0':
              isWorkspaceV2,
          }"
          class="flex w-[13.5rem] shrink-0 flex-col gap-1 border-r border-n-weak bg-n-solid-2/60 p-3"
          :aria-label="t('SIDEBAR.FOCUS_MODE.AREAS')"
          @keydown="onTablistKeydown"
        >
          <div
            class="mb-2 flex items-center gap-2 px-2 pt-1"
            :class="{ 'max-sm:hidden': isWorkspaceV2 }"
          >
            <BrasaMascot :size="32" state="idle" :look="90" class="shrink-0" />
            <div class="flex min-w-0 flex-col">
              <span class="text-[13px] font-bold leading-tight text-n-slate-12">
                {{
                  t(
                    isWorkspaceV2
                      ? 'WORKSPACE_V2.DETAILS'
                      : 'SIDEBAR.FOCUS_MODE.CUSTOMIZE'
                  )
                }}
              </span>
              <span
                class="text-[10px] font-semibold uppercase tracking-[0.13em] text-n-slate-11"
              >
                {{ t('SIDEBAR.FOCUS_MODE.AREAS') }}
              </span>
            </div>
          </div>
          <button
            v-for="id in tabIds"
            :key="id"
            type="button"
            role="tab"
            :data-tab="id"
            :aria-selected="activeTab === id"
            :tabindex="activeTab === id ? 0 : -1"
            class="flex min-h-11 w-full items-center gap-2.5 rounded-xl px-2.5 text-left text-[13px] font-semibold transition-colors"
            :class="
              activeTab === id
                ? 'bg-n-solid-1 text-n-slate-12 shadow-sm ring-1 ring-n-weak'
                : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
            "
            :data-testid="`focus-mode-tab-${id}`"
            @click="selectTab(id)"
          >
            <span
              class="size-4 shrink-0"
              :class="[TAB_ICONS[id], activeTab === id ? 'text-n-iris-11' : '']"
              aria-hidden="true"
            />
            <span class="min-w-0 flex-1 truncate">{{ tabLabel(id) }}</span>
            <span
              v-if="visibleCount(id)"
              class="shrink-0 rounded-full bg-n-alpha-2 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-n-slate-11"
              aria-hidden="true"
            >
              {{ visibleCount(id).shown }}/{{ visibleCount(id).total }}
            </span>
          </button>
        </div>

        <div
          class="flex min-w-0 flex-1 flex-col px-5 pb-3 pt-5"
          role="tabpanel"
          aria-labelledby="focus-mode-settings-title"
        >
          <h3
            id="focus-mode-settings-title"
            class="m-0 text-base font-medium tracking-tight text-n-slate-12"
          >
            {{ paneTitle(activeTab) }}
          </h3>
          <p
            class="mb-4 mt-1 max-w-prose text-xs leading-relaxed text-n-slate-11"
          >
            {{ paneHint(activeTab) }}
          </p>

          <div class="min-h-0 flex-1 overflow-y-auto pr-1">
            <div
              v-if="activeTab === 'theme'"
              role="radiogroup"
              class="grid grid-cols-3 gap-2"
              :aria-label="t('SIDEBAR.FOCUS_MODE.GROUP_THEME')"
            >
              <button
                v-for="scheme in COLOR_SCHEMES"
                :key="scheme"
                type="button"
                role="radio"
                :aria-checked="colorScheme === scheme"
                class="flex flex-col items-stretch gap-2 rounded-2xl border p-2 text-left transition-colors"
                :class="
                  colorScheme === scheme
                    ? 'border-n-iris-9 bg-n-iris-9/10'
                    : 'border-n-weak hover:bg-n-alpha-2'
                "
                :data-testid="`focus-mode-theme-${scheme}`"
                @click="setTheme(scheme)"
              >
                <span
                  class="flex h-14 overflow-hidden rounded-xl border border-n-weak"
                  aria-hidden="true"
                >
                  <span
                    class="flex flex-1 flex-col gap-1 p-2"
                    :class="scheme === 'dark' ? 'bg-[#0B0F16]' : 'bg-white'"
                  >
                    <span
                      class="h-1.5 w-2/3 rounded-full"
                      :class="
                        scheme === 'dark' ? 'bg-white/70' : 'bg-slate-800/80'
                      "
                    />
                    <span
                      class="h-1.5 w-1/2 rounded-full"
                      :class="
                        scheme === 'dark' ? 'bg-white/30' : 'bg-slate-800/30'
                      "
                    />
                  </span>
                  <span
                    v-if="scheme === 'auto'"
                    class="flex flex-1 flex-col gap-1 bg-[#0B0F16] p-2"
                  >
                    <span class="h-1.5 w-2/3 rounded-full bg-white/70" />
                    <span class="h-1.5 w-1/2 rounded-full bg-white/30" />
                  </span>
                </span>
                <span
                  class="text-xs font-semibold"
                  :class="
                    colorScheme === scheme
                      ? 'text-n-iris-11'
                      : 'text-n-slate-12'
                  "
                >
                  {{ themeLabel(scheme) }}
                </span>
              </button>
            </div>

            <div v-if="activeTab === 'list'" class="flex flex-col gap-4">
              <div class="flex flex-col gap-2">
                <span
                  class="text-[11px] font-bold uppercase tracking-[0.12em] text-n-slate-11"
                >
                  {{ t('SIDEBAR.FOCUS_MODE.LIST_MODE_TITLE') }}
                </span>
                <div
                  role="radiogroup"
                  class="grid grid-cols-3 gap-1 rounded-2xl bg-n-alpha-2 p-1"
                  :aria-label="t('SIDEBAR.FOCUS_MODE.LIST_MODE_TITLE')"
                >
                  <button
                    v-for="mode in LIST_MODES"
                    :key="mode.id"
                    type="button"
                    role="radio"
                    :aria-checked="listMode === mode.id"
                    class="flex min-h-10 items-center justify-center gap-1.5 rounded-xl px-2 text-xs font-semibold transition-colors"
                    :class="
                      listMode === mode.id
                        ? 'bg-n-solid-1 text-n-iris-11 shadow-sm'
                        : 'text-n-slate-11 hover:text-n-slate-12'
                    "
                    :data-testid="`focus-mode-list-mode-${mode.id}`"
                    @click="setListMode(mode.id)"
                  >
                    <span
                      class="size-4 shrink-0"
                      :class="mode.icon"
                      aria-hidden="true"
                    />
                    {{ listModeLabel(mode.id) }}
                  </button>
                </div>
                <p class="m-0 text-[11px] text-n-slate-11">
                  {{ t('CHAT_LIST.WORKBENCH.MODE_HIDDEN_HINT') }}
                </p>
              </div>
              <div class="flex flex-col gap-2">
                <span
                  class="text-[11px] font-bold uppercase tracking-[0.12em] text-n-slate-11"
                >
                  {{ t('SIDEBAR.FOCUS_MODE.LIST_FONT_TITLE') }}
                </span>
                <div
                  role="radiogroup"
                  class="grid grid-cols-3 gap-1 rounded-2xl bg-n-alpha-2 p-1"
                  :aria-label="t('SIDEBAR.FOCUS_MODE.GROUP_LIST')"
                >
                  <button
                    v-for="size in CONV_LIST_FONT_SIZES"
                    :key="size"
                    type="button"
                    role="radio"
                    :aria-checked="listFontSize === size"
                    class="flex min-h-10 items-baseline justify-center gap-1.5 rounded-xl px-2 transition-colors"
                    :class="
                      listFontSize === size
                        ? 'bg-n-solid-1 text-n-iris-11 shadow-sm'
                        : 'text-n-slate-11 hover:text-n-slate-12'
                    "
                    :data-testid="`focus-mode-list-font-${size}`"
                    @click="setListFontSize(size)"
                  >
                    <span
                      class="font-semibold"
                      :class="
                        size === 'sm'
                          ? 'text-xs'
                          : size === 'lg'
                            ? 'text-lg'
                            : 'text-sm'
                      "
                      aria-hidden="true"
                    >
                      {{ t('SIDEBAR.FOCUS_MODE.LIST_FONT_SAMPLE') }}
                    </span>
                    <span class="text-[11px] font-semibold">{{
                      listFontLabel(size)
                    }}</span>
                  </button>
                </div>
              </div>
            </div>

            <div
              v-if="activeTab === 'cluster'"
              role="radiogroup"
              class="mx-auto grid w-44 grid-cols-3 gap-1.5"
              :aria-label="t('SIDEBAR.FOCUS_MODE.MENU_SIDE')"
            >
              <template v-for="(side, index) in SIDE_GRID" :key="index">
                <button
                  v-if="side"
                  type="button"
                  role="radio"
                  :aria-checked="menuSide === side"
                  :title="sideLabel(side)"
                  class="flex min-h-12 flex-col items-center justify-center gap-0.5 rounded-xl border text-[10px] font-semibold transition-colors"
                  :class="
                    menuSide === side
                      ? 'border-n-iris-9 bg-n-iris-9 text-white'
                      : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
                  "
                  :data-testid="`focus-mode-menu-side-${side}`"
                  @click="setMenuSide(side)"
                >
                  <span
                    class="size-4"
                    :class="sideIcon(side)"
                    aria-hidden="true"
                  />
                  {{ sideLabel(side) }}
                </button>
                <span v-else aria-hidden="true" />
              </template>
            </div>

            <div v-if="activeItems.length" class="flex flex-col gap-1">
              <div
                v-for="item in activeItems"
                :key="item.name"
                class="flex min-h-11 items-center justify-between gap-3 rounded-xl px-2 hover:bg-n-alpha-2"
              >
                <span class="flex min-w-0 items-center gap-2.5">
                  <span
                    class="flex size-7 shrink-0 items-center justify-center rounded-lg bg-n-alpha-2 text-n-slate-11"
                    aria-hidden="true"
                  >
                    <span class="size-3.5" :class="itemIcon(item)" />
                  </span>
                  <span class="truncate text-sm font-medium text-n-slate-12">
                    {{ labelFor(item) }}
                  </span>
                </span>
                <ToggleSwitch
                  :aria-label="labelFor(item)"
                  :model-value="isVisible(activeGroup.key, item.name)"
                  :data-testid="`focus-mode-toggle-${item.name}`"
                  @update:model-value="
                    value => setVisibility(activeGroup.key, item.name, value)
                  "
                />
              </div>
            </div>
          </div>

          <div
            class="mt-2 flex items-center justify-between gap-2 border-t border-n-weak pt-2.5"
          >
            <template v-if="!confirmingRestore">
              <div class="flex min-w-0 flex-col gap-1">
                <button
                  type="button"
                  class="min-h-9 self-start rounded-full px-2 text-left text-xs font-semibold text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                  data-testid="focus-mode-restore"
                  @click="askRestore"
                >
                  {{ t('SIDEBAR.FOCUS_MODE.RESTORE') }}
                </button>
                <button
                  v-if="!isWorkspaceV2"
                  type="button"
                  class="min-h-9 self-start rounded-full px-2 text-left text-xs font-semibold text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                  data-testid="focus-mode-replay-guide"
                  @click="replayGuide"
                >
                  {{ t('SIDEBAR.FOCUS_MODE.REPLAY_GUIDE') }}
                </button>
                <p class="m-0 text-[11px] text-n-slate-11">
                  {{
                    t(
                      isWorkspaceV2
                        ? 'WORKSPACE_V2.PERSONAL'
                        : 'SIDEBAR.FOCUS_MODE.FOCUS_ONLY'
                    )
                  }}
                </p>
              </div>
              <button
                type="button"
                class="h-9 shrink-0 rounded-full bg-n-slate-12 px-4 text-sm font-medium text-n-background hover:opacity-90"
                data-testid="focus-mode-done"
                @click="close"
              >
                {{ t('SIDEBAR.FOCUS_MODE.DONE') }}
              </button>
            </template>
            <template v-else>
              <p class="m-0 min-w-0 text-xs leading-snug text-n-slate-12">
                {{ t('SIDEBAR.FOCUS_MODE.RESTORE_CONFIRM') }}
              </p>
              <div class="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  class="min-h-9 rounded-full px-3 text-xs font-semibold text-n-slate-11 hover:bg-n-alpha-2"
                  data-testid="focus-mode-restore-cancel"
                  @click="cancelRestore"
                >
                  {{ t('SIDEBAR.FOCUS_MODE.RESTORE_CANCEL') }}
                </button>
                <button
                  type="button"
                  class="min-h-9 rounded-full bg-n-ruby-9 px-3 text-xs font-medium text-white hover:bg-n-ruby-10"
                  data-testid="focus-mode-restore-confirm"
                  @click="restoreDefaults"
                >
                  {{ t('SIDEBAR.FOCUS_MODE.RESTORE_YES') }}
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>
  </TeleportWithDirection>
</template>
